import { useEffect, useMemo, useState } from "react";
import {
  ExternalLink,
  Loader2,
  ShieldCheck,
  TrendingDown,
  TrendingUp,
} from "lucide-react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { getCardTruePrice, type TruePriceResult } from "@/api/market";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";

import { cleanMarketHistory, marketHistoryWindow } from "./history";

type Props = {
  cardId?: number;
  result?: TruePriceResult | null;
  cardFormat?: "raw" | "graded";
  language?: "english" | "japanese" | "chinese";
  condition?: string;
  printing?: string;
  gradingCompany?: string;
  grade?: string;
  chineseScript?: "simplified" | "traditional";
  labelTier?: "standard" | "pristine" | "black_label";
  compact?: boolean;
};

function money(value: number | null) {
  return value == null
    ? "Not available"
    : value.toLocaleString("en-US", {
        style: "currency",
        currency: "USD",
      });
}

function sourceLabel(source: string) {
  return source
    .replace("pokellama_marketplace", "PokeLlama Marketplace")
    .replace("pokellama_sale", "PokeLlama Sale")
    .replace("justtcg", "JustTCG")
    .replace("pricecharting_marketplace", "PriceCharting Marketplace")
    .replace("pricecharting", "PriceCharting")
    .replace("ebay", "eBay");
}

export function TruePricePanel({
  cardId,
  result: providedResult,
  cardFormat = "raw",
  language = "english",
  condition,
  printing,
  gradingCompany,
  grade,
  chineseScript,
  labelTier,
  compact = false,
}: Props) {
  const [result, setResult] = useState<TruePriceResult | null>(
    providedResult ?? null,
  );
  const [loading, setLoading] = useState(Boolean(cardId && !providedResult));
  const [showEvidence, setShowEvidence] = useState(false);
  const [requestFailed, setRequestFailed] = useState(false);
  const [retry, setRetry] = useState(0);

  useEffect(() => {
    setResult(providedResult ?? null);
    setRequestFailed(false);
    if (providedResult || !cardId) {
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    getCardTruePrice(cardId, {
      cardFormat,
      language,
      condition,
      printing,
      gradingCompany,
      grade,
      chineseScript,
      labelTier,
    })
      .then((response) => {
        if (!cancelled) setResult(response.data);
      })
      .catch(() => {
        if (!cancelled) {
          setResult(null);
          setRequestFailed(true);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [
    cardId,
    providedResult,
    cardFormat,
    language,
    condition,
    printing,
    gradingCompany,
    grade,
    chineseScript,
    labelTier,
    retry,
  ]);

  const visibleEvidence = useMemo(() => result?.evidence ?? [], [result]);

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center gap-2 p-4 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          Calculating evidence-backed True Price…
        </CardContent>
      </Card>
    );
  }

  if (!result) {
    return (
      <Card>
        <CardContent className="p-4 text-sm text-muted-foreground">
          {requestFailed
            ? "Unable to load pricing. This request failed; it does not mean the card has no valuation evidence."
            : "True Price is unavailable until enough exact-match evidence is found."}
          {requestFailed && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="ml-2"
              onClick={() => setRetry((value) => value + 1)}
            >
              Retry pricing
            </Button>
          )}
        </CardContent>
      </Card>
    );
  }

  const signal = result.signal.recommendation;
  const summary = result.evidenceSummary;
  const includedSales = result.evidence.filter(
    (item) =>
      item.included && ["completed_sale", "market_sale"].includes(item.type),
  );
  const saleCount = summary?.completedSales.count ?? includedSales.length;
  const history = cleanMarketHistory(result.providerHistory);
  const trends = [7, 30, 90].map((days) => ({
    days,
    window: marketHistoryWindow(history, days),
  }));
  const identity = result.identity;
  const basis =
    result.truePrice == null
      ? "No eligible valuation evidence"
      : saleCount
        ? "Recent sales support this estimate"
        : "Guide-based estimate · no eligible recent sales";
  const groups = [
    {
      title: "Completed sale records",
      rows: visibleEvidence.filter((item) =>
        ["completed_sale", "market_sale"].includes(item.type),
      ),
    },
    {
      title: "Market guides",
      rows: visibleEvidence.filter((item) => item.type === "price_guide"),
    },
    {
      title: "Asking prices · shopping context",
      rows: visibleEvidence.filter((item) => item.type === "active_listing"),
    },
  ];

  return (
    <Card className="border-holo-blue/30">
      <CardHeader className={compact ? "p-4 pb-2" : "pb-3"}>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className="flex items-center gap-2">
              <ShieldCheck className="h-5 w-5 text-holo-blue" />
              <h3 className="font-black">PokeLlama True Price</h3>
            </div>
            <p className="mt-1 text-xs text-muted-foreground">
              Estimated market value · sales and market guides · asking prices
              separate
            </p>
          </div>
          <Badge variant="outline">
            {result.confidence.band} confidence in evidence ·{" "}
            {result.confidence.score}/100
          </Badge>
        </div>
      </CardHeader>
      <CardContent className={compact ? "space-y-3 p-4 pt-0" : "space-y-4"}>
        <p className="text-sm font-medium">{basis}</p>
        {identity && (
          <p className="text-xs text-muted-foreground">
            Selected variant:{" "}
            {[
              identity.language === "chinese"
                ? `${identity.chineseScript ?? "Unconfirmed script"} Chinese`
                : identity.language,
              identity.cardFormat,
              identity.printing,
              identity.cardFormat === "raw"
                ? identity.condition
                : [identity.gradingCompany, identity.grade, identity.labelTier]
                    .filter(Boolean)
                    .join(" "),
            ]
              .filter(Boolean)
              .join(" · ")}
          </p>
        )}
        {result.status === "incomplete_identity" && (
          <p className="text-sm text-muted-foreground">
            Confirm the exact language, Chinese script, printing, condition or
            slab grade and label tier before valuation.
          </p>
        )}
        <p className="text-xs text-muted-foreground">
          Confidence reflects available evidence, not a guaranteed sale price.
        </p>
        <p className="text-xs text-muted-foreground">
          Last valuation calculation:{" "}
          {new Date(result.calculatedAt).toLocaleString()}
        </p>
        {result.confidence.sourceCount === 1 && result.truePrice != null && (
          <p className="text-xs text-muted-foreground">
            One eligible source supports this value; independent source
            confirmation is unavailable.
          </p>
        )}
        {result.truePrice == null && (
          <p className="text-sm text-muted-foreground">
            No exact valuation is available. Reference guides and asking prices
            below remain separate.
          </p>
        )}
        {result.observedAt && (
          <p className="text-xs text-muted-foreground">
            Latest included source refresh:{" "}
            {new Date(result.observedAt).toLocaleString()}
          </p>
        )}
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <div>
            <p className="text-xs text-muted-foreground">Market estimate</p>
            <p className="text-xl font-black text-holo-blue">
              {money(result.truePrice)}
            </p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground">
              Observed evidence range
            </p>
            <p className="font-semibold">
              {money(result.range.low)}–{money(result.range.high)}
            </p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground">Evidence used</p>
            <p className="font-semibold">
              {result.confidence.includedObservations} observations ·{" "}
              {result.confidence.sourceCount} sources
            </p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground">Market signal</p>
            <div className="flex items-center gap-1 font-semibold capitalize">
              {signal === "buy" && (
                <TrendingDown className="h-4 w-4 text-green-600" />
              )}
              {signal === "wait" && (
                <TrendingUp className="h-4 w-4 text-amber-600" />
              )}
              {signal.replace("_", " ")}
            </div>
          </div>
        </div>

        {result.signal.bestAvailablePrice != null && (
          <p className="text-sm text-muted-foreground">
            Lowest verified asking price:{" "}
            <span className="font-semibold text-foreground">
              {money(result.signal.bestAvailablePrice)}
            </span>
            {result.signal.discountToTruePricePercent != null &&
              ` · ${Math.abs(result.signal.discountToTruePricePercent).toFixed(1)}% ${
                result.signal.discountToTruePricePercent >= 0
                  ? "below"
                  : "above"
              } True Price`}
          </p>
        )}

        <div className="rounded-lg border bg-muted/20 p-3">
          <p className="text-sm font-semibold">
            Recent completed sales · {saleCount} eligible records
          </p>
          {summary?.completedSales.latestSaleAt && (
            <p className="mt-1 text-xs text-muted-foreground">
              Latest sale:{" "}
              {new Date(
                summary.completedSales.latestSaleAt,
              ).toLocaleDateString()}
            </p>
          )}
          {summary && (
            <div className="mt-3 grid grid-cols-3 gap-2">
              {summary.completedSales.periods.map((period) => (
                <div key={period.days}>
                  <p className="text-xs text-muted-foreground">
                    Last {period.days} days
                  </p>
                  <p className="text-sm font-semibold">{period.count} sales</p>
                  <p className="text-xs text-muted-foreground">
                    Median: {money(period.median)}
                  </p>
                </div>
              ))}
            </div>
          )}
          <p className="mt-2 text-xs text-muted-foreground">
            {saleCount
              ? "Counts reflect the eligible retrieved sample, not total marketplace volume. Guides are not individual sale records."
              : "No eligible recent transactions were found for this exact variant. Market guides may still support an estimate."}
          </p>
          {result.methodology.ebaySoldHistoryConnected === false && (
            <p className="mt-1 text-xs text-muted-foreground">
              eBay completed-sale history is not connected. eBay asking prices
              do not affect valuation.
            </p>
          )}
        </div>

        <div className="rounded-lg border bg-muted/20 p-3">
          <p className="text-sm font-semibold">
            Historical market-guide trends
          </p>
          <p className="text-xs text-muted-foreground">
            Changes use recorded JustTCG prices for this exact variant, ending
            at the latest historical observation. They are not completed-sales
            trends.
          </p>
          <div className="mt-2 grid grid-cols-3 gap-2">
            {trends.map(({ days, window }) => (
              <div key={days}>
                <p className="text-xs text-muted-foreground">
                  {days}-day change
                </p>
                <p className="text-sm font-semibold">
                  {window
                    ? `${window.changePercent > 0 ? "+" : ""}${window.changePercent.toFixed(2)}%`
                    : "Not available"}
                </p>
                {window && (
                  <p className="text-xs text-muted-foreground">
                    Through {new Date(window.end * 1000).toLocaleDateString()}
                  </p>
                )}
              </div>
            ))}
          </div>
          {trends.some((item) => !item.window) && (
            <p className="mt-2 text-xs text-muted-foreground">
              Missing periods have insufficient recorded history; no trend is
              inferred.
            </p>
          )}
        </div>

        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => setShowEvidence((current) => !current)}
        >
          {showEvidence
            ? "Hide supporting evidence"
            : "View supporting evidence"}
        </Button>

        {showEvidence && (
          <div className="space-y-3 border-t pt-3">
            {summary && (
              <div>
                <p className="mb-2 text-sm font-semibold">
                  Source coverage for this variant
                </p>
                <div className="grid gap-2 sm:grid-cols-2">
                  {summary.coverage.map((source) => (
                    <div
                      key={source.source}
                      className="rounded-lg border p-2 text-xs"
                    >
                      <p className="font-semibold">
                        {sourceLabel(source.source)}
                      </p>
                      <p className="text-muted-foreground">
                        {source.status === "used"
                          ? "Contributes to valuation"
                          : source.status === "reference_only"
                            ? "Reference evidence only"
                            : "No matching evidence"}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {history.length > 1 && (
              <div>
                <p className="text-sm font-semibold">
                  JustTCG market-price history
                </p>
                <p className="mb-2 text-xs text-muted-foreground">
                  Provider market estimates over time; these points are not
                  individual sales.
                </p>
                <div
                  className="h-44 w-full"
                  role="img"
                  aria-label="JustTCG historical market price chart"
                >
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={history}>
                      <XAxis
                        dataKey="t"
                        tickFormatter={(value) =>
                          new Date(Number(value) * 1000).toLocaleDateString(
                            undefined,
                            { month: "short", day: "numeric" },
                          )
                        }
                        minTickGap={40}
                      />
                      <YAxis
                        tickFormatter={(value) => `$${value}`}
                        width={60}
                      />
                      <Tooltip
                        labelFormatter={(value) =>
                          new Date(Number(value) * 1000).toLocaleDateString()
                        }
                        formatter={(value: number) => [
                          money(value),
                          "Market guide",
                        ]}
                      />
                      <Line
                        dataKey="p"
                        stroke="#2563eb"
                        strokeWidth={2}
                        dot={false}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )}
            <div className="grid gap-2 sm:grid-cols-2">
              {result.sources.map((source) => (
                <div
                  key={source.source}
                  className="rounded-lg border bg-muted/20 p-3 text-sm"
                >
                  <p className="font-semibold">{sourceLabel(source.source)}</p>
                  <p className="text-xs text-muted-foreground">
                    {source.included}/{source.observations} used
                    {source.weightPercent != null &&
                      ` · ${source.weightPercent.toFixed(1)}% of valuation`}
                    {source.average != null &&
                      ` · Avg. ${money(source.average)}`}
                  </p>
                </div>
              ))}
            </div>
            <div className="max-h-72 space-y-2 overflow-y-auto">
              {groups.map((group) => (
                <div key={group.title} className="space-y-2">
                  <p className="text-sm font-semibold">{group.title}</p>
                  {!group.rows.length && (
                    <p className="text-xs text-muted-foreground">
                      No matching records available.
                    </p>
                  )}
                  {group.rows.map((item) => (
                    <div
                      key={item.id}
                      className="flex items-center justify-between gap-3 rounded-lg border p-3 text-sm"
                    >
                      <div className="min-w-0">
                        <p className="truncate font-medium">
                          {item.title || sourceLabel(item.source)}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {sourceLabel(item.source)} ·{" "}
                          {item.type.replaceAll("_", " ")} ·{" "}
                          {item.included
                            ? "Used in valuation"
                            : "Not used in valuation"}
                        </p>
                        {item.exclusionReason && (
                          <p className="text-xs text-muted-foreground">
                            {item.exclusionReason}
                          </p>
                        )}
                        <p className="text-xs text-muted-foreground">
                          {item.occurredAt ? "Sale date" : "Source observed"}:{" "}
                          {new Date(
                            item.occurredAt ?? item.observedAt,
                          ).toLocaleString()}
                        </p>
                      </div>
                      <div className="flex shrink-0 items-center gap-2">
                        <span className="font-bold">
                          {money(
                            item.type === "active_listing"
                              ? item.totalPrice
                              : item.itemPrice,
                          )}
                        </span>
                        {item.url && (
                          <a
                            href={item.url}
                            target="_blank"
                            rel="noreferrer"
                            aria-label="Open supporting evidence"
                            className="text-holo-blue"
                          >
                            <ExternalLink className="h-4 w-4" />
                          </a>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ))}
            </div>
            <p className="text-xs text-muted-foreground">
              Completed sales receive more weight than price guides. Asking
              prices and broader guide references receive no valuation weight.
              Valuations use item prices; shopping costs include known shipping.
              Low-confidence and statistical outliers are excluded.
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
